import { ArrowLeft, Loader2, Monitor, Plug, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { OrgAdminLayout, StatusBadge, TierBadge } from "@lexiun/org-admin";
import type { OrgUser } from "@lexiun/org-admin";
import { AssignInstanceDialog } from "../components/platform/AssignInstanceDialog";
import { RenameOrgDialog } from "../components/platform/RenameOrgDialog";
import { ChangeAcceptedEmailDialog } from "../components/platform/ChangeAcceptedEmailDialog";
import { canChangeAcceptedEmail } from "../lib/change-user-email";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { useRuntimeVersions } from "../hooks/useRuntimeVersions";
import { useRuntimeContext } from "../hooks/useRuntimeContext";
import { useGatewayRpc } from "../hooks/useGatewayRpc";
import { VersionSkewBadge } from "../components/shared/VersionSkewBadge";
import type { Organization } from "../types/platform";

export function OrgUsers() {
  const { orgId } = useParams<{ orgId: string }>();
  const api = usePlatformApi();
  // §22 — per-user runtime version + skew. Joined by userId from the
  // separate batch source (the OrgUser type lives in @lexiun/org-admin
  // and is out of scope to extend). Omitted until the endpoint is live.
  const { byUserId, targetVersion, available: versionsAvailable } = useRuntimeVersions();
  // Per-row Connect → Runtime affordance — mirrors the RuntimeFleet
  // schema (Connect button → connecting → Runtime link to "/" + ×).
  // Replaced the previous "Manage Instance" button (which opened the
  // runtime's OWN OpenClaw admin SPA in a new tab — a confusing
  // overlap with LexPortal's Runtime page that operators expected here).
  const {
    target,
    selectRuntime,
    clearRuntime,
    loading: connecting,
    error: connectError,
  } = useRuntimeContext();
  const { state: gatewayState } = useGatewayRpc();

  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignUser, setAssignUser] = useState<OrgUser | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [changeEmailUser, setChangeEmailUser] = useState<OrgUser | null>(null);
  const [usersReloadKey, setUsersReloadKey] = useState(0);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        setLoading(true);
        const orgRes = await api.getOrg(orgId);
        setOrg(orgRes.data);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load org");
      } finally {
        setLoading(false);
      }
    })();
  }, [api, orgId]);

  const handleConnect = useCallback(
    (user: OrgUser) => {
      if (!orgId) return;
      void selectRuntime(
        user.orgId,
        user.userId,
        org?.name ?? user.orgId,
        user.displayName,
        org?.slug ?? user.orgId,
      );
    },
    [orgId, org, selectRuntime],
  );

  const getToken = useCallback(() => api.resolveToken(), [api]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading...
      </div>
    );
  }

  if (!orgId) return null;

  const orgHeader = (
    <div className="mb-6">
      <Link
        to="/orgs"
        className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Organizations
      </Link>
      {org && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* The name IS the control (same idiom as the invitation-email
                edit in org-admin's UserTable): click the value you intend to
                change, rather than hunting an unlabelled pencil elsewhere.
                The slug stays plain text beside it — it is not editable. */}
            <h1 className="text-xl font-semibold">
              <button
                type="button"
                onClick={() => setRenameOpen(true)}
                title="Rename this organization (display name only — the slug never changes)"
                data-testid="org-rename-trigger"
                className="rounded underline decoration-dotted decoration-slate-400 underline-offset-4 transition-colors hover:text-primary dark:decoration-slate-600"
              >
                {org.name}
              </button>
            </h1>
            <StatusBadge status={org.status} />
            <TierBadge tier={org.settings.tier} />
            <span className="text-sm text-slate-400 dark:text-slate-500">({org.slug})</span>
          </div>
          <Link
            to={`/orgs/${orgId}/clients`}
            className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            data-testid="org-clients-link"
          >
            Clients →
          </Link>
        </div>
      )}
      {error && (
        <div className="mt-2 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">{error}</div>
      )}
      {connectError && (
        <div
          className="mt-2 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
          data-testid="orguser-connect-error"
        >
          Could not connect to runtime: {connectError}
        </div>
      )}
    </div>
  );

  /** Per-row Connect → Runtime affordance (same schema as RuntimeFleet). */
  const renderConnectAction = (user: OrgUser) => {
    const isActive =
      target?.userId === user.userId && target?.orgId === user.orgId;
    const selConnected = isActive && gatewayState === "connected";
    if (isActive) {
      return (
        <>
          {selConnected ? (
            <Link
              to="/"
              data-testid={`orguser-runtime-${user.userId}`}
              title="Open the Runtime page for this connected runtime"
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
            >
              <Monitor className="h-3 w-3" /> Runtime
            </Link>
          ) : (
            <span
              data-testid={`orguser-connecting-${user.userId}`}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              Connecting…
            </span>
          )}
          <button
            type="button"
            onClick={() => clearRuntime()}
            data-testid={`orguser-clear-${user.userId}`}
            title="Disconnect this runtime"
            className="rounded-md border border-slate-300 p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <X className="h-3 w-3" />
          </button>
        </>
      );
    }
    return (
      <button
        type="button"
        onClick={() => handleConnect(user)}
        disabled={connecting}
        data-testid={`orguser-connect-${user.userId}`}
        title="Connect to this user's runtime (once connected, the cell becomes a link to the Runtime page)"
        className="inline-flex items-center gap-1 rounded-md border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
      >
        {connecting ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Plug className="h-3 w-3" />
        )}
        {connecting ? "Connecting…" : "Connect"}
      </button>
    );
  };

  /** Platform-only action buttons per user row. */
  const renderUserActions = (user: OrgUser) => (
    <>
      {/* §22 — runtime build version + skew (joined by userId). */}
      {versionsAvailable && byUserId.has(user.userId) && (
        <div className="mr-2 flex items-center gap-1.5" data-testid={`user-version-${user.userId}`}>
          <span className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
            {byUserId.get(user.userId)!.running?.version ?? "unknown"}
          </span>
          <VersionSkewBadge
            skew={byUserId.get(user.userId)!.skew}
            targetVersion={targetVersion}
            className="px-1.5 py-0 text-[9px]"
          />
        </div>
      )}

      {/* Instance endpoint — the VALUE is the control. A separate button
          labelled with a verb competed with the edit-USER button beside it
          and cost a wrong click; clicking the address you want to change has
          no such ambiguity. The unprovisioned placeholder is clickable too,
          so "assign" and "edit" are the same gesture. */}
      <div className="mr-2 flex items-center">
        <button
          type="button"
          onClick={() => setAssignUser(user)}
          title={
            user.containerEndpoint
              ? `Edit this user's runtime container endpoint (${user.containerEndpoint})`
              : "Assign a runtime container endpoint"
          }
          data-testid={`assign-instance-${user.userId}`}
          className={`rounded px-1 py-0.5 text-xs underline decoration-dotted underline-offset-2 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 ${
            user.containerEndpoint
              ? "font-mono text-slate-500 dark:text-slate-400"
              : "italic text-slate-400 dark:text-slate-500"
          }`}
        >
          {user.containerEndpoint
            ? user.containerEndpoint.replace(/^https?:\/\//, "").slice(0, 40)
            : "Not provisioned"}
        </button>
      </div>

      {/* Change sign-in email — ACCEPTED users only, PLATFORM_ADMIN only.
          Rendered from LexPortal's own renderUserActions rather than added to
          org-admin, so this never enters the bundle LexIDE ships to org
          admins. org-admin's EditUserDialog keeps the email LOCKED for these
          users and that stays correct: this is the separate deliberate path,
          not a relaxation of it. */}
      {canChangeAcceptedEmail(user) && (
        <button
          type="button"
          onClick={() => setChangeEmailUser(user)}
          title="Change this user's Clerk sign-in email (platform admin)"
          data-testid={`change-email-${user.userId}`}
          className="mr-2 rounded px-1.5 py-0.5 text-xs text-slate-500 underline decoration-dotted underline-offset-2 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          Change email
        </button>
      )}

      {/* Connect → Runtime dual button — same schema as RuntimeFleet
          (the per-row action that wires this user's runtime into the
          active context; once connected, the cell becomes a Link to
          "/" — the Runtime page). */}
      {renderConnectAction(user)}
    </>
  );

  return (
    <div>
      <OrgAdminLayout
        // Remounted after a verified email change so the row refetches from
        // the server. The list is owned by org-admin and has no imperative
        // refresh hook; a key bump is honest about that rather than patching
        // a row we do not own the state of.
        key={usersReloadKey}
        orgId={orgId}
        getToken={getToken}
        basePath={`/orgs/${orgId}`}
        apiBaseUrl="/lexserver/api"
        orgHeader={orgHeader}
        renderUserActions={renderUserActions}
        // PLATFORM_ADMIN context — surface the per-row "Accept invitation"
        // admin button. lexserver enforces the PLATFORM_ADMIN guard
        // server-side; the prop just controls UI visibility.
        allowAcceptInvitation
      />

      <AssignInstanceDialog
        user={assignUser}
        onClose={() => setAssignUser(null)}
        onAssigned={() => { setAssignUser(null); window.location.reload(); }}
      />

      <ChangeAcceptedEmailDialog
        open={changeEmailUser !== null}
        onOpenChange={(o) => { if (!o) setChangeEmailUser(null); }}
        orgId={orgId}
        user={changeEmailUser}
        // Only fires after the dialog has VERIFIED the server echoed the new
        // address, so the refetch is confirming a known-good change rather
        // than hoping. Closing here too: the dialog closes itself, and this
        // keeps the trigger state from re-opening it on the next render.
        onChanged={() => {
          setChangeEmailUser(null);
          setUsersReloadKey((k) => k + 1);
        }}
      />

      <RenameOrgDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        org={org ? { orgId: org.orgId, name: org.name, slug: org.slug } : null}
        // The dialog only calls this after VERIFYING the server echoed the new
        // name, so patching local state is reporting what the server returned,
        // not assuming the write landed. No refetch needed.
        //
        // Matched on orgId: never paint one org's new name onto whichever org
        // happens to be loaded now.
        onRenamed={({ orgId: renamed, name }) =>
          setOrg((prev) => (prev && prev.orgId === renamed ? { ...prev, name } : prev))
        }
      />
    </div>
  );
}
